import React, { useEffect, useState } from 'react';
import { Button, Dropdown, Menu, Select, Space, Table, message, Spin } from 'antd';
import { WebApiMemberDtoMemberDto, WebApiServicesDataTransferObjectsGroupServiceGroupMemberDto, WebApiServicesDataTransferObjectsGroupServiceGroupResultDto, WebApiModelsMembershipProduct, WebApiServicesDataTransferObjectsMemberServiceFilterLogic, WebApiModelsView, WebApiModelsMemberField } from '../../Repository/eventbookyapi';
import Search from 'antd/lib/input/Search';
import { SubscriptionLevel } from '../../Types/SubscriptionLevel';
import { TextService } from '../../Services/TextService';
import AddMember from './AddMember';
import { MemberService } from '../../Services/MemberService';
import { DeleteOutlined, DollarCircleOutlined, DownloadOutlined, FileOutlined, MoreOutlined, SettingOutlined } from '@ant-design/icons';
import { mapGroupToListItem, mapMemberStatusToListItem, mapMembershipProductsToListItem } from '../../Mappers/mapGroupToListItem';
import { BookyButton } from '../Buttons/BookyButton';
import { MemberTable } from '../Table/MemberTable';
import { BookySelectPaymentStatus } from '../Forms/BookyPaymentStatusSelect';
import { MemberPaymentStatus } from '../../Types/MemberPaymentStatus';
import { useNavigate } from 'react-router-dom';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';
import { MemberStatusType } from '../../Types/MemberStatusType';
import { PaymentConfiguration } from '../../Types/PaymentConfiguration';
import Cookies from 'universal-cookie';
import FullPageSpinner from '../Spinner/FullPageSpinner';
import { SavedSorting } from '../../Types/SavedSorting';
import { AdvancedMemberFilter, FilterRequest } from '../Filters/AdvancedMemberFilter';
import { ViewService } from '../../Services/ViewService';
import { MemberFieldService } from '../../Services/MemberFieldService';
import { CustomTable } from '../Table/CustomTable';
import { CustomerPreferencesHelper } from '../../Helpers/CustomerPreferencesHelper';

export const MemberList = (props: any) => {
    // Always call the hook, but handle undefined memberFieldSettings inside the hook
    const [groups, setGroups] = useState([] as WebApiServicesDataTransferObjectsGroupServiceGroupResultDto[]);
    const [selectedGroup, setSelectedGroup] = useState({} as WebApiServicesDataTransferObjectsGroupServiceGroupResultDto);
    const [memberList, setMemberList] = useState([] as WebApiMemberDtoMemberDto[]);
    const [filteredMembers, setFilteredMembers] = useState([] as WebApiMemberDtoMemberDto[]);
    const [freeTextSearch, setFreeTextSearch] = useState('');
    const [selectedPaymentStatus, setSelectedPaymentStatus] = useState(undefined as MemberPaymentStatus | undefined);
    const [selectedMemberStatus, setSelectedMemberStatus] = useState(MemberStatusType.Active as MemberStatusType);
    const [selectedMembershipProduct, setSelectedMembershipProduct] = useState(-1 as number);
    const [pageSizePreference, setPageSizePreference] = useState(10 as number);
    const [savedSorting, setSavedSorting] = useState(undefined as SavedSorting | undefined);
    const [showFullPageSpinner, setShowFullPageSpinner] = useState(false as boolean);
    const [activeFilters, setActiveFilters] = useState<FilterRequest | undefined>(undefined);
    const [availableViews, setAvailableViews] = useState<WebApiModelsView[]>([]);
    const [selectedView, setSelectedView] = useState<WebApiModelsView | null>(null);
    const [memberFields, setMemberFields] = useState<WebApiModelsMemberField[]>([]);
    const [isLoadingViews, setIsLoadingViews] = useState(true);

    const navigation = useNavigate();

    const GetText = TextService.GetText;

    const loadMembers = () => {
        props.loadMembers();
    }

    useEffect(() => {
        var members = props.memberResult?.members!;
        setMemberList(members);
        filterMembers(members, selectedPaymentStatus, selectedGroup?.id, freeTextSearch, selectedMemberStatus, selectedMembershipProduct);

        props.memberResult.groups.forEach((element: any) => {
            if (element.isDefaultGroup) {
                element.name = GetText("MemberList_DefaultAllMembersName");
            }
        });

        setGroups(props.memberResult.groups!);
        setSelectedGroup(props.memberResult.groups![0]);

        const sorter = CustomerPreferencesHelper.getSorter();
        if (sorter) {
            setSavedSorting(sorter);
        } else {
            setSavedSorting({ columnKey: "uniqueId", sortingDirection: "ascend" });
        }

        const pageSize = CustomerPreferencesHelper.getPageSize();
        if (pageSize) {
            setPageSizePreference(pageSize);
        }

    }, [props])

    // Load views and member fields for Premium/Enterprise users
    useEffect(() => {
        const loadViewsAndFields = async () => {
            if (props.user.subscriptionLevel === SubscriptionLevel.Premium || props.user.subscriptionLevel === SubscriptionLevel.Enterprise) {
                try {
                    // Load member fields
                    const fields = await MemberFieldService.GetMemberFields();
                    setMemberFields(fields);

                    // Load views and filter to Member View type (viewType === 0)
                    const views = await ViewService.GetAllViews();
                    const memberViews = views.filter(view => view.viewType === 0);
                    setAvailableViews(memberViews);

                    // Restore saved view preference
                    const savedViewId = CustomerPreferencesHelper.getSelectedView();
                    if (savedViewId && savedViewId !== 'default') {
                        const savedView = memberViews.find(v => v.id === savedViewId);
                        if (savedView) {
                            setSelectedView(savedView);
                        } else {
                            // View was deleted, reset to default
                            CustomerPreferencesHelper.setSelectedView('default');
                        }
                    }
                } catch (error) {
                    console.error('Failed to load views or member fields:', error);
                } finally {
                    setIsLoadingViews(false);
                }
            } else {
                setIsLoadingViews(false);
            }
        };

        loadViewsAndFields();
    }, [props.user.subscriptionLevel]);

    const handleChange = (value: { value: string; label: React.ReactNode }) => {
        groups.forEach((group: WebApiServicesDataTransferObjectsGroupServiceGroupResultDto) => {
            if (group.id === Number(value)) {
                setSelectedGroup(group);
                filterMembers(memberList, selectedPaymentStatus, Number(value), freeTextSearch, selectedMemberStatus, selectedMembershipProduct);
            }
        });
    };

    const handleMemberStatusChange = (value: MemberStatusType | null | undefined) => {
        filterMembers(memberList, selectedPaymentStatus, selectedGroup?.id, freeTextSearch, value!, selectedMembershipProduct);
        setSelectedMemberStatus(value!);
    };

    const handleMembershipProductChange = (value: number) => {
        filterMembers(memberList, selectedPaymentStatus, selectedGroup?.id, freeTextSearch, selectedMemberStatus, value);
        setSelectedMembershipProduct(value);
    };

    const handlePageSizeChanged = (value: number) => {
        setPageSizePreference(value);
    };

    const filterMembers = (memberList: WebApiMemberDtoMemberDto[], status?: MemberPaymentStatus, groupId?: number, freeTextSearch?: string, selectedStatus?: MemberStatusType, membershipProductId?: number) => {
        let filteredMemberList: WebApiMemberDtoMemberDto[] = memberList;

        filteredMemberList = memberList.filter((member: WebApiMemberDtoMemberDto) => member.status === selectedStatus);

        if (groupId && groupId != -1) {
            filteredMemberList = filteredMemberList.filter((member: WebApiMemberDtoMemberDto) =>
                member.groups?.includes(Number(groupId)));
        }

        if (membershipProductId && membershipProductId != -1) {
            filteredMemberList = filteredMemberList.filter((member: WebApiMemberDtoMemberDto) =>
                member.membershipProduct === membershipProductId ||
                member.membershipProductAddOn?.includes(membershipProductId));
        }

        if (status === MemberPaymentStatus.Payed) {
            filteredMemberList = filteredMemberList.filter((member: WebApiMemberDtoMemberDto) => member.isPaidCurrentYear);
        }
        if (status === MemberPaymentStatus.UnPayed) {
            filteredMemberList = filteredMemberList.filter((member: WebApiMemberDtoMemberDto) => !member.isPaidCurrentYear);
        }
        if (status === MemberPaymentStatus.UnPayedLastYear) {
            filteredMemberList = filteredMemberList.filter((member: WebApiMemberDtoMemberDto) => !member.isPaidLastYear);
        }

        if (freeTextSearch) {
            filteredMemberList = filteredMemberList.filter((member: WebApiServicesDataTransferObjectsGroupServiceGroupMemberDto) =>
                member.email?.toLowerCase()?.includes(freeTextSearch) ||
                member.uniqueId?.toLowerCase()?.includes(freeTextSearch) ||
                member.firstName?.toLowerCase()?.includes(freeTextSearch) ||
                member.lastName?.toLowerCase().includes(freeTextSearch) ||
                member.company?.toLowerCase().includes(freeTextSearch) ||
                member.phoneNumber?.toLowerCase().includes(freeTextSearch)
            );
        }

        setFilteredMembers(filteredMemberList);
    }

    const buildFilterRequest = (filterRequest?: FilterRequest) => {
        const mappedFilters = filterRequest?.filters.map(filter => ({
            ...filter,
            logic: (filter.logic === 'And' ? 0 : 1) as WebApiServicesDataTransferObjectsMemberServiceFilterLogic
        })) || [];

        return {
            status: selectedPaymentStatus,
            isAllMembers: !selectedGroup.id || selectedGroup.id == -1 ? true : false,
            groupsIds: selectedGroup.id && selectedGroup.id != -1 ? [selectedGroup.id] : undefined,
            filters: mappedFilters
        };
    }

    const handleAdvancedFilterChange = async (filterRequest?: FilterRequest) => {
        if (!filterRequest || filterRequest.filters.length === 0) {
            // If no filters, reset to basic filters
            setActiveFilters(undefined);
            filterMembers(memberList, selectedPaymentStatus, selectedGroup.id, freeTextSearch, selectedMemberStatus, selectedMembershipProduct);
            return;
        }

        setActiveFilters(filterRequest);

        try {
            const requestDto = buildFilterRequest(filterRequest);
            const response = await MemberService.GetFilteredMembers(requestDto);

            if (response) {
                setFilteredMembers(response);
            }
        } catch (error) {
            console.error('Error filtering members:', error);
            message.error('Failed to filter members');
        }
    }

    const downloadFilteredMembers = async () => {
        if (props.user?.subscriptionLevel === SubscriptionLevel.Free) {
            message.warning(GetText("MyEventDetails_SorryOnlyPremium"));
            return;
        }
        setShowFullPageSpinner(true);
        try {
            const requestDto = buildFilterRequest(activeFilters);
            const data = await MemberService.GetFilteredMembersAsFile({
                filteredMemberIds: filteredMembers.map(m => m.memberId!),
                includeCustomLists: false
            });

            const url = window.URL.createObjectURL(await data.raw.blob());
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Members_Filtered_${Date.now()}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading members:', error);
            message.error('Failed to download members');
        } finally {
            setShowFullPageSpinner(false);
        }
    }

    const onSelectPaymentChanged = async (paymentStatus: MemberPaymentStatus) => {
        setSelectedPaymentStatus(paymentStatus);
        filterMembers(memberList, paymentStatus, selectedGroup.id, freeTextSearch, selectedMemberStatus, selectedMembershipProduct);
    };

    const groupMenu = () => {
        return (<Menu>
            <Menu.Item><BookyButton type="dashed" style={{ width: "250px", textAlign: "left" }} onClick={() => navigation("/" + getCurrentLanguage() + "/uploadmembers")} icon={<FileOutlined />} text={GetText("UploadMembers_UploadFileText")} /></Menu.Item>
            <Menu.Item><BookyButton type="dashed" style={{ width: "250px", textAlign: "left" }} onClick={() => navigation("/" + getCurrentLanguage() + "/uploadpayments")} icon={<FileOutlined />} text={GetText("UploadPaymentData_UploadFileText")} /></Menu.Item>
        </Menu>);
    }

    return (
        <>
            {showFullPageSpinner ? <FullPageSpinner /> : ""}
            <Space direction="vertical">
                <Space wrap>
                    <AddMember user={props.user} membershipProducts={props.membershipProducts} nextMemberIdSuggestion={props.memberResult.nextMemberIdSuggestion} memberFieldSettings={props.memberFieldSettings} loadMembers={loadMembers} members={props.memberResult.members} groups={props.memberResult.groups} />
                    {props.hasPingPaymentIntegration ? <BookyButton type="primary" onClick={() => navigation("/" + getCurrentLanguage() + "/createpaymentlinks")} icon={<DollarCircleOutlined />} text={"Skapa betallänkar"} /> : ""}

                </Space>
            </Space>

            {!props.user.hasOnlyReadRights ? <div style={{ float: 'right' }}>
                {(props.user.subscriptionLevel === SubscriptionLevel.Premium || props.user.subscriptionLevel === SubscriptionLevel.Enterprise) && availableViews.length > 0 && (
                    <Select
                        style={{ width: 200, marginRight: "10px" }}
                        placeholder="Select View"
                        value={selectedView?.id || 'default'}
                        onChange={(value) => {
                            const viewValue = value === 'default' ? 'default' : Number(value);
                            CustomerPreferencesHelper.setSelectedView(viewValue);

                            if (value === 'default') {
                                setSelectedView(null);
                            } else {
                                const view = availableViews.find(v => v.id === value);
                                setSelectedView(view || null);
                            }
                        }}
                        options={[
                            { value: 'default', label: GetText('MemberList_StandardView') },
                            ...availableViews.map(view => ({ value: view.id, label: view.name }))
                        ]}
                    />
                )}
                <BookyButton
                    type="default"
                    onClick={downloadFilteredMembers}
                    icon={<DownloadOutlined />}
                    text={GetText("MemberList_Download")}
                />
                <Dropdown overlay={groupMenu}>
                    <Button style={{ marginLeft: "10px" }}>
                        <Space>
                            <MoreOutlined />{"Fler alternativ"}
                        </Space>
                    </Button>
                </Dropdown>
            </div> : ""}

            <br></br>

            <Space direction="horizontal">
                <Space wrap>
                    <Search className='memberlist-filter'
                        placeholder={GetText("MemberList_SearchGroupMember")}
                        value={freeTextSearch}
                        onChange={e => {
                            const currValue = e.target.value.toLowerCase();
                            setFreeTextSearch(currValue);
                            filterMembers(memberList, selectedPaymentStatus, selectedGroup.id, currValue, selectedMemberStatus, selectedMembershipProduct);
                        }}
                    />
                </Space>

                <Space wrap>
                    <Select
                        className='memberlist-filter memberlist-filter-small'
                        value={{ value: selectedGroup.id?.toString()!, label: selectedGroup.name }}
                        options={mapGroupToListItem(groups)}
                        onChange={handleChange}
                    />
                </Space>

                <Select
                    className='memberlist-filter memberlist-filter-small'
                    value={selectedMemberStatus}
                    options={mapMemberStatusToListItem()}
                    onChange={handleMemberStatusChange}
                />

                {props.membershipProducts && props.membershipProducts.length > 0 && (
                    <Select
                        className='memberlist-filter memberlist-filter-small'
                        value={selectedMembershipProduct}
                        options={mapMembershipProductsToListItem(props.membershipProducts || [])}
                        onChange={handleMembershipProductChange}
                    />
                )}

                {props.user.paymentConfiguration == PaymentConfiguration.YearlyPayments ? <BookySelectPaymentStatus className='memberlist-filter memberlist-filter-small' onSelectPaymentChanged={onSelectPaymentChanged} /> : ""}
            </Space>

            {(props.user.subscriptionLevel === SubscriptionLevel.Premium || props.user.subscriptionLevel === SubscriptionLevel.Enterprise) && (
                <AdvancedMemberFilter
                    memberFieldSettings={props.memberFieldSettings}
                    onFilterChange={handleAdvancedFilterChange}
                />
            )}

            <div style={{ width: "100%", paddingTop: "10px" }}>
                <i>{GetText("MemberList_MembersFoundCount1")} {filteredMembers.length} {GetText("MemberList_MembersFoundCount2")}</i>
            </div>

            {!isLoadingViews && (selectedView ? (
                <CustomTable
                    view={selectedView}
                    members={filteredMembers}
                    memberFields={memberFields}
                    user={props.user}
                    membershipProducts={props.membershipProducts}
                    groups={props.memberResult.groups}
                    loadMembers={loadMembers}
                    enableRowClick={true}
                    pageSizePreference={pageSizePreference}
                    handlePageSizeChanged={handlePageSizeChanged}
                />
            ) : (
                <MemberTable
                    savedSorting={savedSorting}
                    handlePageSizeChanged={handlePageSizeChanged}
                    hasPingPaymentIntegration={props.hasPingPaymentIntegration}
                    pageSizePreference={pageSizePreference}
                    user={props.user}
                    members={filteredMembers}
                    memberFieldSettings={props.memberFieldSettings}
                    membershipProducts={props.membershipProducts}
                    groups={props.memberResult.groups}
                    loadMembers={loadMembers}
                    enableRowClick={true}
                />
            ))}
        </>

    );
};

export default MemberList;